export interface StockAdjustment {
  id: string;
  item_id: string;
  item_name: string;
  item_sku: string;
  adjustment_type: AdjustmentType;
  quantity: number;
  unit: string;
  batch_id?: string;
  batch_number?: string;
  warehouse_id: string;
  warehouse_name: string;
  location_id?: string;
  location_name?: string;
  remarks: string;
  adjustment_date: number;
  authorized_by: string;
  authorized_by_name: string;
  created_at: number;
  created_by: string;
  updated_at: number;
  updated_by: string;
  status: "pending" | "approved" | "rejected";
  reference_number: string;
}

export type AdjustmentType =
  | "spoilage"
  | "expired"
  | "breakage"
  | "manual"
  | "custom";

export interface StockAdjustmentFormData {
  item_id: string;
  adjustment_type: AdjustmentType;
  quantity: number;
  batch_id?: string;
  warehouse_id: string;
  location_id?: string;
  remarks: string;
  adjustment_date: number;
  custom_reason?: string; // For when adjustment_type is 'custom'
}

export interface StockAdjustmentFilters {
  search?: string;
  adjustment_type?: AdjustmentType | "all";
  warehouse_id?: string;
  status?: "pending" | "approved" | "rejected" | "all";
  start_date?: number;
  end_date?: number;
  item_id?: string;
}

export interface StockAdjustmentListResponse {
  items: StockAdjustment[];
  total: number;
  page: number;
  limit: number;
}

export interface ItemMaster {
  id: string;
  name: string;
  sku: string;
  unit: string;
  current_stock: number;
  category: string;
  is_batch_tracked: boolean;
  min_stock_level: number;
  max_stock_level: number;
}

export interface BatchInfo {
  id: string;
  batch_number: string;
  quantity: number;
  remaining_quantity: number;
  expiry_date?: number;
  created_date: number;
}

export interface Warehouse {
  id: string;
  name: string;
  code: string;
  address?: string;
  is_active: boolean;
}

export interface Location {
  id: string;
  name: string;
  code: string;
  warehouse_id: string;
  description?: string;
  is_active: boolean;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface StockAdjustmentValidation {
  is_valid: boolean;
  errors: ValidationError[];
  warnings?: string[];
}

export const ADJUSTMENT_TYPE_OPTIONS = [
  {
    value: "spoilage",
    label: "Spoilage",
    description: "Items damaged or spoiled",
  },
  {
    value: "expired",
    label: "Expired",
    description: "Items past expiration date",
  },
  {
    value: "breakage",
    label: "Breakage",
    description: "Items broken or damaged",
  },
  {
    value: "manual",
    label: "Manual Correction",
    description: "Manual stock count adjustment",
  },
  {
    value: "custom",
    label: "Custom",
    description: "Other reasons (specify in remarks)",
  },
] as const;

export const ADJUSTMENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "warning" },
  { value: "approved", label: "Approved", color: "success" },
  { value: "rejected", label: "Rejected", color: "error" },
] as const;
